import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCheck, Trash2, ExternalLink, Inbox } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
];

export default function NotificationsCenter() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const r = await api.get("/notifications", { params: { unread_only: f === "unread", limit: 200 } });
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
      setTotal(r.data.total || 0);
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    toast.success("All notifications marked read");
    load(filter);
  };

  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    if (n.customer_id) navigate(`/app/customers/${n.customer_id}`);
    else load(filter);
  };

  const removeItem = async (e, n) => {
    e.stopPropagation();
    await api.delete(`/notifications/${n.id}`).catch(() => {});
    load(filter);
  };

  return (
    <div data-testid="notifications-center">
      <PageHeader
        title="Notifications"
        subtitle={`${total} total • ${unread} unread`}
        actions={
          <Button onClick={markAll} disabled={!unread} data-testid="mark-all-read-btn" className="bg-emerald-600 hover:bg-emerald-700">
            <CheckCheck size={16} className="mr-2" /> Mark all read
          </Button>
        }
      />

      <div className="flex gap-2 mb-5" data-testid="notif-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`notif-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filter === f.key ? "bg-[#0A192F] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            {f.label}
            {f.key === "unread" && unread > 0 && (
              <span className="ml-2 bg-rose-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">{unread}</span>
            )}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400 text-sm animate-pulse">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-14 text-center text-slate-400" data-testid="notif-empty">
            <Inbox size={40} className="mx-auto mb-3 opacity-40" />
            <div className="text-sm">{filter === "unread" ? "No unread notifications." : "No notifications yet."}</div>
          </div>
        ) : (
          items.map((n) => (
            <div
              key={n.id}
              onClick={() => openItem(n)}
              data-testid="notif-row"
              className={`group flex items-start gap-3 px-5 py-4 border-b last:border-0 cursor-pointer transition-colors hover:bg-slate-50 ${
                n.read ? "" : "bg-blue-50/60"
              }`}
            >
              <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-blue-500"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-800">{n.message}</div>
                <div className="text-xs text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {n.customer_id && <ExternalLink size={15} className="text-slate-400" />}
                <button
                  onClick={(e) => removeItem(e, n)}
                  data-testid="notif-delete-btn"
                  className="p-1.5 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-500"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
